import React, {
  createContext,
  FC,
  PropsWithChildren,
  useRef,
  useContext,
  useEffect,
} from 'react'
import { StoreApi, useStore as useZustandStore } from 'zustand'
import type { SearchStoreState, StoreProviderProps } from './types'
import { shouldReinitialiseState } from './helpers'
import { createSearchStore } from './createSearchStore'
import { getNextState } from './getNextState'

const SearchStoreContext = createContext<StoreApi<SearchStoreState> | null>(
  null,
)

export const SearchStoreProvider: FC<PropsWithChildren<StoreProviderProps>> = ({
  initialState,
  children,
}) => {
  const store = useRef<StoreApi<SearchStoreState>>()
  if (!store.current) {
    store.current = createSearchStore(initialState)
  }

  useEffect(() => {
    /* istanbul ignore next */
    if (!store.current) return

    const newState = initialState
    const currentState = store.current.getState() as SearchStoreState
    const reinitialiseState = shouldReinitialiseState({
      store,
      newState,
    })

    if (!reinitialiseState) return

    const nextState = getNextState(currentState, newState)
    store.current.setState(nextState)
  }, [initialState])

  return (
    <SearchStoreContext.Provider value={store.current}>
      {children}
    </SearchStoreContext.Provider>
  )
}

export const useSearchStore = <T,>(
  selector: (store: SearchStoreState) => T,
) => {
  const store = useContext(SearchStoreContext)

  // istanbul ignore next
  if (!store) {
    throw new Error('Missing SearchStoreContextProvider')
  }

  return useZustandStore(store, selector)
}
